/**
 * CreateIncidentForm.tsx — New incident creation form
 *
 * Purpose:    Allows operators to open a new incident with title, severity
 *             (P1–P4), and optional runbook URL.
 * Inputs:     onCreate callback from useIncidentData
 * Outputs:    Inline form; calls onCreate then resets on success
 * Constraints: Title is required; runbook URL is optional and validated as URL
 */
import { useState } from 'react'
import type { IncidentSeverity, CreateIncidentPayload } from '../../hooks/useIncidentData'

export function CreateIncidentForm({
  onCreate,
}: {
  onCreate: (p: CreateIncidentPayload) => Promise<{ ok: boolean; error?: string }>
}) {
  const [title, setTitle] = useState('')
  const [severity, setSeverity] = useState<IncidentSeverity>('p2')
  const [runbookUrl, setRunbookUrl] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    setSubmitting(true)
    setSubmitError(null)
    const res = await onCreate({ title: title.trim(), severity, runbookUrl: runbookUrl || undefined })
    setSubmitting(false)
    if (res.ok) {
      setTitle('')
      setRunbookUrl('')
    } else {
      setSubmitError(res.error ?? 'Failed to create incident')
    }
  }

  return (
    <form onSubmit={(e) => { void handleSubmit(e) }} className="rounded-lg border border-brand-subtle bg-brand-surface/50 p-4">
      <h3 className="mb-3 text-sm font-semibold text-brand-ink">Open new incident</h3>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs text-brand-muted" htmlFor="inc-title">Title</label>
          <input
            id="inc-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="API latency spike in production"
            className="w-full rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none"
            required
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-brand-muted" htmlFor="inc-severity">Severity</label>
          <select
            id="inc-severity"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as IncidentSeverity)}
            className="w-full rounded border border-brand-card bg-brand-surface-2 px-2 py-1.5 text-sm text-brand-ink focus:border-sky-500 focus:outline-none"
          >
            <option value="p1">P1 — Critical</option>
            <option value="p2">P2 — High</option>
            <option value="p3">P3 — Medium</option>
            <option value="p4">P4 — Low</option>
          </select>
        </div>
        <div className="sm:col-span-3">
          <label className="mb-1 block text-xs text-brand-muted" htmlFor="inc-runbook">
            Runbook URL (optional)
          </label>
          <input
            id="inc-runbook"
            type="url"
            value={runbookUrl}
            onChange={(e) => setRunbookUrl(e.target.value)}
            placeholder="https://wiki.example.com/runbooks/api-latency"
            className="w-full rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none"
          />
        </div>
      </div>
      {submitError && <p className="mt-2 text-xs text-red-700 dark:text-red-400">{submitError}</p>}
      <button
        type="submit"
        disabled={submitting || !title.trim()}
        className="mt-3 rounded-md bg-red-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
      >
        {submitting ? 'Opening…' : 'Open incident'}
      </button>
    </form>
  )
}
